import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MessageSquare,
  Send,
  User
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ClientMessagesTab({ getAuthHeaders }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [trainer, setTrainer] = useState(null);
  const messagesEndRef = useRef(null);

  // Fetch trainer (first trainer user) and conversation
  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      
      // First, get conversations to see if we already have one with a trainer
      const convRes = await axios.get(`${API}/messages/conversations`, { headers });
      const trainerConv = convRes.data.find(c => c.partner_role === "trainer");
      
      if (trainerConv) {
        setTrainer({
          id: trainerConv.partner_id,
          name: trainerConv.partner_name
        });
        
        // Fetch messages with trainer
        const msgRes = await axios.get(`${API}/messages/conversation/${trainerConv.partner_id}`, { headers });
        setMessages(msgRes.data.messages);
      } else {
        // No existing conversation - find the trainer account
        try {
          const trainerRes = await axios.get(`${API}/messages/find-trainer`, { headers });
          if (trainerRes.data.trainer_id) {
            setTrainer({
              id: trainerRes.data.trainer_id,
              name: trainerRes.data.trainer_name
            });
          }
        } catch (err) {
          console.log("No trainer found yet");
        }
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
    // Poll for new messages every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    // If we don't have a trainer ID yet, we need to find one
    if (!trainer?.id) {
      toast.error("Unable to find trainer. Please contact support.");
      return;
    }

    setSending(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/messages/send`, {
        recipient_id: trainer.id,
        content: newMessage.trim()
      }, { headers });

      setMessages(prev => [...prev, res.data]);
      setNewMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Format timestamp
  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="card-premium p-8 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-olive border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-stone">Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="card-premium overflow-hidden">
      <div className="flex flex-col h-[500px]">
        {/* Header */}
        <div className="p-4 border-b border-sand flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-olive/20 flex items-center justify-center">
            <User className="w-5 h-5 text-olive" />
          </div>
          <div>
            <h4 className="font-medium text-charcoal">Message Your Trainer</h4>
            <p className="text-xs text-stone">We typically respond within 24 hours</p>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center text-stone">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No messages yet</p>
                <p className="text-sm mt-1">Send a message to get in touch with your trainer</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => {
                const isOwn = msg.sender_role === "client";
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-2xl ${
                        isOwn
                          ? 'bg-olive text-white rounded-br-sm'
                          : 'bg-sand text-charcoal rounded-bl-sm'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-stone'}`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <form onSubmit={handleSendMessage} className="p-4 border-t border-sand">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1"
              disabled={sending || !trainer?.id}
              data-testid="client-message-input"
            />
            <Button 
              type="submit" 
              className="btn-primary"
              disabled={!newMessage.trim() || sending || !trainer?.id}
              data-testid="client-send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
